import {
  persistentKeyboardFocus,
  persistentKeyboardKeepFocused,
} from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { cn } from "@-/com.npmjs.cn";
import Animated, { LinearTransition } from "@-/com.npmjs.react-native-reanimated";
import { ActivityIndicator, Platform, Pressable, View } from "@-/com.npmjs.react-native";
import type { ReactNode } from "react";

const CONTINUOUS_BORDER_CURVE_STYLE = { borderCurve: "continuous" } as const;

/**
 * Shared answer-input row used by both the meaning and pronunciation drill footers so the two
 * rows match exactly: a floating dismiss-button slot on the left (reserved while the keyboard is
 * open), and a pill-shaped input with an optional reveal icon inside its right edge.
 *
 * The header-sized chevron dismiss button itself is rendered at the app root;
 * this row only reserves the horizontal slot for it.
 */
export function AnswerInputRow({
  accessibilityValue = "",
  children,
  fillHeight = false,
  isDismissButtonVisible,
  isRevealButtonVisible = true,
  onReveal,
  revealDisabled = false,
  revealLoading = false,
  trailingActions,
}: {
  /** Visible input text exposed on the row because the overlaid native field is grouped beneath it. */
  accessibilityValue?: string;
  children: ReactNode;
  /** Fill the stationary deck footer instead of sizing the row independently. */
  fillHeight?: boolean;
  /** Whether the floating dismiss button is showing — reserves the left slot when true. */
  isDismissButtonVisible: boolean;
  isRevealButtonVisible?: boolean;
  onReveal: () => void;
  revealDisabled?: boolean;
  revealLoading?: boolean;
  trailingActions?: ReactNode;
}) {
  const { diameter } = useToolbarGridMetrics();
  // The floating dismiss button matches the header controls, with a 9px gap before the input
  // (same inset as the floating tab bar's expanded-input dismiss slot).
  const inputContainerMarginLeft = isDismissButtonVisible ? diameter + 9 : 0;

  return (
    <View className={cn("relative w-full", fillHeight && "h-full")} pointerEvents="box-none">
      <Animated.View
        className={fillHeight ? "flex-1" : undefined}
        layout={LinearTransition.duration(90)}
        pointerEvents="box-none"
      >
        <View className={cn("flex-row", fillHeight ? "flex-1" : "pt-1")} pointerEvents="auto">
          <ToolbarGlassSurface
            className={cn("flex-1 flex-row items-center rounded-full", fillHeight && "h-full")}
            interactive={false}
            style={[
              CONTINUOUS_BORDER_CURVE_STYLE,
              { height: fillHeight ? undefined : diameter, marginLeft: inputContainerMarginLeft },
            ]}
          >
            <Pressable
              accessibilityLabel="Answer input"
              // This label DOES reach Android — but never as the bare string "Answer input", because
              // Android concatenates the label and the `accessibilityValue` below into one
              // comma-joined `content-desc`. The node's accessibility text is the literal
              // `"Answer input, "` (trailing comma-space) while the field is empty, and
              // `"Answer input, abcdefg"` once the learner types. Maestro matches `text:` as a
              // FULL-STRING regex, so a selector of `"Answer input"` cannot match either form — that
              // alone is why `answer-input-caret-position.yaml` kept failing on healthy drill
              // screens; the flow now selects `"Answer input.*"`.
              //
              // Two earlier fixes misdiagnosed this. 68719d66 added `accessibilityRole="button"`
              // believing the label was being dropped by node reduction; comparing the hierarchy
              // dumps either side of it (runs 33242112495 without the role, 33257603676 with it)
              // shows `"Answer input, "` published in BOTH, so the role was irrelevant here and its
              // later revert was harmless. It is deliberately NOT re-added: a role of `button`
              // reports this text field as a Button to TalkBack, which is worse for real users and
              // fixes nothing. Keep the label as-is for iOS/VoiceOver and the `getByLabelText`
              // fixtures; the selector, not the label, was the bug.
              accessibilityValue={{ text: accessibilityValue ?? "" }}
              className="h-full min-w-0 flex-1 flex-row"
              onPress={persistentKeyboardFocus}
              // Android deliberately omits the press-in focus: `onPressIn` fires on ACTION_DOWN,
              // i.e. before the native EditText has seen ACTION_UP and placed the caret at the
              // tapped offset. Focusing that early re-runs `requestFocus` underneath the pending
              // tap and snaps the caret back to the end, so tapping mid-string to fix a typo never
              // appeared to register. The EditText raises the IME by itself once tapped, so the
              // `onPress` handler below is only a backstop there. iOS keeps the eager focus: its
              // UITextField sits behind the Pressable and needs the explicit first-responder call.
              onPressIn={Platform.OS === "android" ? undefined : persistentKeyboardFocus}
            >
              {children}
            </Pressable>
            {trailingActions ??
              (isRevealButtonVisible ? (
                <Pressable
                  accessibilityLabel="Reveal answer"
                  accessibilityRole="button"
                  accessibilityState={{
                    busy: revealLoading,
                    disabled: revealDisabled || revealLoading,
                  }}
                  className="h-full shrink-0 items-center justify-center"
                  disabled={revealDisabled || revealLoading}
                  onPress={onReveal}
                  onPressIn={persistentKeyboardKeepFocused}
                  style={{ width: diameter }}
                >
                  {revealLoading ? (
                    <ActivityIndicator size="small" testID="reveal-answer-loading-indicator" />
                  ) : (
                    <Icon className="text-foreground" name="LuChevronRight" size={18} />
                  )}
                </Pressable>
              ) : null)}
          </ToolbarGlassSurface>
        </View>
      </Animated.View>
    </View>
  );
}
